/**
 * Compass logic (HEX-124 / ADR-0018 §6).
 *
 * Pure, side-effect-free helpers:
 *   - `nearestUnliberatedTown` — settlement-lattice scan + state check.
 *   - `compassAngle`           — screen-space direction from player to target.
 *   - `hexesWithinRadius`      — the +2 visibility ring around a liberated town.
 */

import type { Tile } from '../../core/tile';
import { getTownState } from '../../lifecycle/townState';
import { nearbySettlementAnchors } from '../../worldgen/frontierSettlement';

/** Hex radius to reveal around a liberated town (ADR-0018 §6). */
export const TOWN_VISIBILITY_RADIUS = 2;

/**
 * Settlement-lattice search radius. 320 hexes covers well beyond any
 * reasonable play session; the lattice scan is O(radius²/blockSize²) so this
 * is fast even at distance.
 */
export const COMPASS_TOWN_SEARCH_RADIUS = 320;

const SQRT3 = Math.sqrt(3);

/**
 * Nearest town in the settlement lattice that is currently `'evil'`.
 *
 * Tiles loaded in the world store are checked against their persisted state;
 * un-loaded tiles (beyond the render buffer) are assumed evil — the compass
 * naturally guides the player toward them.
 *
 * Returns `null` when every town within `COMPASS_TOWN_SEARCH_RADIUS` is
 * liberated — i.e. the player has cleared the neighbourhood and the compass
 * should hide.
 */
export function nearestUnliberatedTown(
  pq: number,
  pr: number,
  worldSeed: number,
  getTileAt: (hex: { q: number; r: number }) => Tile | null,
): { q: number; r: number } | null {
  const anchors = nearbySettlementAnchors(pq, pr, worldSeed, COMPASS_TOWN_SEARCH_RADIUS);
  let best: { q: number; r: number } | null = null;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const anchor of anchors) {
    const tile = getTileAt(anchor);
    // Loaded + confirmed liberated or under-threat → skip (services are up)
    if (tile !== null && tile.type === 'town' && getTownState(tile) !== 'evil') continue;
    const dq = anchor.q - pq;
    const dr = anchor.r - pr;
    const d = (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
    if (d < bestDist) {
      bestDist = d;
      best = { q: anchor.q, r: anchor.r };
    }
  }
  return best;
}

/**
 * Screen-space angle (radians, atan2 convention) from player `(pq, pr)` to
 * target `(tq, tr)`.  Uses the pointy-top hex pixel projection:
 *   x = √3·q + √3/2·r,  y = 3/2·r  (layout.size cancels).
 */
export function compassAngle(pq: number, pr: number, tq: number, tr: number): number {
  const dx = SQRT3 * (tq - pq) + (SQRT3 / 2) * (tr - pr);
  const dy = (3 / 2) * (tr - pr);
  return Math.atan2(dy, dx);
}

/**
 * All hex coordinates within `radius` of `(q, r)` (inclusive centre).
 * Used to enumerate the +2 visibility ring around a liberated town.
 */
export function hexesWithinRadius(
  q: number,
  r: number,
  radius: number,
): { q: number; r: number }[] {
  const out: { q: number; r: number }[] = [];
  for (let dq = -radius; dq <= radius; dq++) {
    const drMin = Math.max(-radius, -dq - radius);
    const drMax = Math.min(radius, -dq + radius);
    for (let dr = drMin; dr <= drMax; dr++) {
      out.push({ q: q + dq, r: r + dr });
    }
  }
  return out;
}
